<template>
  <div
    class="work-instructions-container"
    data-testid="editor-work-instructions-page"
  >
    <WorkInstructionsHeader
      :hide-package-dropdown="true"
      :hide-actions="true"
      :custom-breadcrumbs="currentWorkInstructionName"
      :force-product-list-text="'Template List'"
      :back-to-work-instructions-list="true"
    />

    <div class="wrapper">
      <WIStepModal
        v-if="showAddWIStepModal"
        :initialData="selectedStepForEdit"
        :isEditMode="isEditingWorkInstructionStep"
        @close="showAddWIStepModal = false"
        @save="onSaveWorkInstructionStep"
        @onDelete="onDeleteWorkInstructionStep"
      />

      <div v-if="currentPackage" class="items-host">
        <InstructionCardsList
          :instructions="workInstructions"
          :expanded-instructions="expandedInstructions"
          :show-status-badge="showStatusBadge"
          :show-add-step-button="true"
          card-test-id="editor-wi-card"
          step-card-test-id="editor-wi-step-card"
          @toggle="onToggleInstructionEvent"
          @edit-step="onEditWorkInstructionStepEvent"
          @add-step="onShowAddWIStepModal"
        />

        <div v-if="workInstructions.length === 0" class="empty-state">
          There are no instructions in the selected work instruction package.
        </div>
      </div>

      <div v-else class="empty-state">Work instruction package not found.</div>
    </div>
  </div>
</template>

<script>
import { v4 } from 'uuid';
import client from '../../config/api';
import WorkInstructionsHeader from '../Shared/WorkInstructions/Header';
import WIStepModal from '../Shared/WorkInstructions/WIStepModal';
import InstructionCardsList from '../Shared/WorkInstructions/InstructionCardsList';

const uuid = v4;

export default {
  name: 'EditorStandardWorkInstructionView',
  props: {
    showStatusBadge: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    WorkInstructionsHeader,
    WIStepModal,
    InstructionCardsList,
  },
  data() {
    return {
      currentPackage: null,
      workInstructions: [],
      expandedInstructions: {},
      showAddWIStepModal: false,
      instructionIdToEdit: null,
      selectedStepForEdit: null,
    };
  },
  computed: {
    currentWorkInstructionName() {
      return this.currentPackage?.title || '';
    },
    isEditingWorkInstructionStep() {
      return !!this.selectedStepForEdit;
    },
  },
  watch: {
    '$route.params.packageId': {
      immediate: true,
      async handler() {
        await this.loadPackageView();
      },
    },
  },
  methods: {
    isWorkInstructionType(wiPackage) {
      const type = (wiPackage?.type || '').toString().trim();
      return type === 'standard_work_instruction_package';
    },

    normalizeId(value) {
      if (value == null) {
        return '';
      }

      return String(value).trim();
    },

    packageRouteIds(wiPackage) {
      return [wiPackage?.uuid, wiPackage?.id, wiPackage?._id]
        .map((value) => this.normalizeId(value))
        .filter(Boolean);
    },

    async loadPackageView() {
      const { packageId } = this.$route.params;
      if (!packageId) {
        this.currentPackage = null;
        this.workInstructions = [];
        return;
      }

      const response = await client.get('/work-instructions-package');
      const standardPackages = (response?.data || []).filter(
        (wiPackage) =>
          wiPackage.isStandard && this.isWorkInstructionType(wiPackage)
      );

      const normalizedPackageId = this.normalizeId(packageId);
      const selectedPackage = standardPackages.find((wiPackage) =>
        this.packageRouteIds(wiPackage).includes(normalizedPackageId)
      );

      this.currentPackage = selectedPackage || null;

      if (!this.currentPackage) {
        this.workInstructions = [];
        return;
      }

      const backendPackageId = this.currentPackage._id;

      const instructionsResponse = await client.get(
        `/work-instructions/package/${backendPackageId}`
      );

      this.workInstructions = (instructionsResponse?.data || []).sort(
        (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)
      );
    },

    onToggleInstruction(instructionId, isOpen) {
      this.$set(this.expandedInstructions, instructionId, isOpen);
    },

    onToggleInstructionEvent({ instructionId, isOpen }) {
      this.onToggleInstruction(instructionId, isOpen);
    },

    onShowAddWIStepModal(instructionId) {
      this.instructionIdToEdit = instructionId;
      this.selectedStepForEdit = null;
      this.showAddWIStepModal = true;
    },

    onEditWorkInstructionStep(step, instructionId) {
      this.instructionIdToEdit = instructionId;
      this.selectedStepForEdit = {
        ...step,
      };
      this.showAddWIStepModal = true;
    },

    onEditWorkInstructionStepEvent({ step, instructionId }) {
      this.onEditWorkInstructionStep(step, instructionId);
    },

    async onSaveWorkInstructionStep(stepToAdd) {
      const instructionId = this.instructionIdToEdit;
      this.showAddWIStepModal = false;

      if (!instructionId) {
        return;
      }

      if (this.isEditingWorkInstructionStep) {
        await client.put(
          `/work-instructions/step/${instructionId}/${this.selectedStepForEdit._id}`,
          {
            ...this.selectedStepForEdit,
            ...stepToAdd,
          }
        );

        this.selectedStepForEdit = null;
        this.$set(this.expandedInstructions, instructionId, true);
        await this.loadPackageView();
        return;
      }

      await client.post(`/work-instructions/step/${instructionId}`, {
        _id: uuid(),
        description: stepToAdd?.description || '',
      });

      this.$set(this.expandedInstructions, instructionId, true);
      await this.loadPackageView();
    },

    async onDeleteWorkInstructionStep() {
      const instructionId = this.instructionIdToEdit;
      const stepId = this.selectedStepForEdit?._id;
      this.showAddWIStepModal = false;

      if (!instructionId || !stepId) {
        return;
      }

      await client.delete(`/work-instructions/step/${instructionId}/${stepId}`);
      this.selectedStepForEdit = null;
      this.$set(this.expandedInstructions, instructionId, true);
      await this.loadPackageView();
    },
  },
};
</script>

<style lang="scss" scoped>
.work-instructions-container {
  height: calc(100vh - 50px);
  width: 100%;
}

.wrapper {
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  text-align: center;
  justify-content: center;
  width: 100%;
  background-color: #dddddd;
}

.items-host {
  flex: 1;
  overflow: hidden;
}

.empty-state {
  margin: auto;
  font-size: 18px;
  color: #555;
}
</style>
